import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Input } from '@/shared/components/Input';
import { Select } from '@/shared/components/Select';
import { Button } from '@/shared/components/Button';
import { ApiError } from '@/lib/api-client';
import { useOppositions } from '@/features/director-professors/hooks/useSubjects';
import { useShifts } from '@/features/director-schedules/hooks/useShifts';
import { useCreateStudent, useNextStudentNumber } from '../hooks/useStudents';
/** Hoy en ISO local (para el default del input date). */
function todayISO() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}
export function CreateStudentForm({ onSuccess }) {
    const [serverError, setServerError] = useState(null);
    const [created, setCreated] = useState(null);
    const createStudent = useCreateStudent();
    const { data: nextNumber } = useNextStudentNumber();
    const { data: oppositions = [], isLoading: oppLoading } = useOppositions();
    const enabledOppositions = oppositions.filter((o) => o.is_enabled);
    const { register, handleSubmit, setValue, watch, formState: { errors }, } = useForm({
        defaultValues: {
            student_number: undefined,
            first_name: '',
            last_name: '',
            email: '',
            dni: '',
            phone: '',
            opposition_id: '',
            shift_id: '',
            address: '',
            birth_date: '',
            start_date: todayISO(),
        },
    });
    const oppositionId = watch('opposition_id');
    const { data: shifts = [], isLoading: shiftsLoading } = useShifts(oppositionId || null);
    // Pre-fill student_number with the suggested next free integer.
    useEffect(() => {
        if (nextNumber?.next_number)
            setValue('student_number', nextNumber.next_number);
    }, [nextNumber, setValue]);
    // Auto-pick first enabled opposition.
    useEffect(() => {
        const first = enabledOppositions[0];
        if (!oppositionId && first)
            setValue('opposition_id', first.id);
    }, [enabledOppositions, oppositionId, setValue]);
    // Reset shift when opposition changes (the previous shift no longer applies).
    useEffect(() => {
        setValue('shift_id', '');
    }, [oppositionId, setValue]);
    function onSubmit(values) {
        setServerError(null);
        if (!values.opposition_id || !values.shift_id) {
            setServerError('Selecciona oposición y turno.');
            return;
        }
        const dto = {
            first_name: values.first_name.trim(),
            last_name: values.last_name.trim(),
            email: values.email.trim(),
            dni: values.dni.trim(),
            opposition_id: values.opposition_id,
            shift_id: values.shift_id,
            ...(values.phone.trim() ? { phone: values.phone.trim() } : {}),
            ...(values.address.trim() ? { address: values.address.trim() } : {}),
            ...(values.birth_date ? { birth_date: values.birth_date } : {}),
            ...(values.start_date ? { start_date: values.start_date } : {}),
            ...(values.student_number != null ? { student_number: values.student_number } : {}),
        };
        createStudent.mutate(dto, {
            onSuccess: (student) => {
                // Show the temporary password so the director can pass the credentials on
                // (the student was created with a temp password and must change it on first login).
                if (student.temp_password) {
                    setCreated({ email: dto.email, password: student.temp_password });
                }
                else {
                    onSuccess();
                }
            },
            onError: (err) => {
                setServerError(err instanceof ApiError ? err.message : 'Ha ocurrido un error.');
            },
        });
    }
    const shiftPlaceholder = !oppositionId
        ? 'Selecciona oposición primero'
        : shiftsLoading
            ? 'Cargando…'
            : shifts.length === 0
                ? 'No hay turnos en esta oposición'
                : 'Seleccionar turno';
    if (created) {
        return (_jsxs("div", { className: "flex flex-col gap-4", children: [_jsxs("div", { className: "rounded-xl border border-green-200 bg-green-50 p-4 text-sm dark:border-green-900 dark:bg-green-950/40", children: [_jsx("p", { className: "font-semibold text-green-800 dark:text-green-300", children: "Alumno creado correctamente" }), _jsx("p", { className: "mt-1 text-gray-600 dark:text-gray-400", children: "Entrega estas credenciales al alumno. La contrase\u00F1a es temporal: deber\u00E1 cambiarla al iniciar sesi\u00F3n. No volver\u00E1 a mostrarse." }), _jsxs("dl", { className: "mt-3 space-y-1 font-mono text-sm break-all text-gray-900 dark:text-gray-100", children: [_jsxs("div", { children: [_jsx("span", { className: "text-gray-500 dark:text-gray-400", children: "Email: " }), created.email] }), _jsxs("div", { children: [_jsx("span", { className: "text-gray-500 dark:text-gray-400", children: "Contrase\u00F1a: " }), created.password] })] })] }), _jsx(Button, { type: "button", onClick: onSuccess, className: "w-full", children: "Hecho" })] }));
    }
    return (_jsxs("form", { onSubmit: handleSubmit(onSubmit), className: "flex flex-col gap-4", noValidate: true, children: [_jsx(Input, { label: "N\u00FAmero de alumno", type: "number", min: 1, error: errors.student_number?.message, hint: "Sugerido autom\u00E1ticamente con el primer n\u00FAmero libre. Editable.", ...register('student_number', {
                    valueAsNumber: true,
                    min: { value: 1, message: 'Debe ser un entero positivo' },
                }) }), _jsxs("div", { className: "grid gap-4 sm:grid-cols-2", children: [_jsx(Input, { label: "Nombre", error: errors.first_name?.message, ...register('first_name', { required: 'El nombre es obligatorio' }) }), _jsx(Input, { label: "Apellidos", error: errors.last_name?.message, ...register('last_name', { required: 'Los apellidos son obligatorios' }) })] }), _jsx(Input, { label: "Email", type: "email", error: errors.email?.message, ...register('email', {
                    required: 'El email es obligatorio',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email no válido' },
                }) }), _jsx(Input, { label: "DNI", error: errors.dni?.message, hint: "Ejemplo: 12345678A", ...register('dni', { required: 'El DNI es obligatorio' }) }), _jsx(Input, { label: "Tel\u00E9fono (opcional)", type: "tel", placeholder: "612345678", error: errors.phone?.message, ...register('phone', {
                    pattern: { value: /^[6789]\d{8}$/, message: 'El teléfono debe ser español (9 dígitos, empieza por 6/7/8/9)' },
                }) }), _jsx(Select, { label: "Oposici\u00F3n", disabled: oppLoading, options: [
                    { value: '', label: oppLoading ? 'Cargando…' : 'Seleccionar oposición' },
                    ...enabledOppositions.map((o) => ({ value: o.id, label: o.name })),
                ], error: errors.opposition_id?.message, ...register('opposition_id', { required: 'Selecciona una oposición' }) }), _jsx(Select, { label: "Turno", disabled: !oppositionId || shiftsLoading, options: [
                    { value: '', label: shiftPlaceholder },
                    ...shifts.map((s) => ({ value: s.id, label: s.name })),
                ], error: errors.shift_id?.message, ...register('shift_id', { required: 'Selecciona un turno' }) }), _jsx(Input, { label: "Direcci\u00F3n (opcional)", ...register('address') }), _jsx(Input, { label: "Fecha de nacimiento (opcional)", type: "date", ...register('birth_date') }), _jsxs("div", { children: [_jsx(Input, { label: "Fecha de inicio", type: "date", ...register('start_date') }), _jsx("p", { className: "mt-1 text-xs text-gray-500 dark:text-gray-400", children: "Por defecto hoy. Si empieza m\u00E1s adelante, no aparecer\u00E1 al pasar lista ni se le generar\u00E1n mensualidades hasta que llegue ese mes." })] }), serverError && (_jsx("p", { role: "alert", className: "text-sm text-red-600 dark:text-red-400", children: serverError })), _jsx(Button, { type: "submit", loading: createStudent.isPending, className: "w-full", children: "Crear alumno" })] }));
}
//# sourceMappingURL=CreateStudentForm.js.map